import { useCallback, useState, useMemo } from "react";

import { Formik, type FormikHelpers } from "formik";

import {
  useGetCampaignLandingsQuery,
  useCreateCampaignLandingMutation,
  useUpdateCampaignLandingMutation,
} from "app/apiSlice";
import { getChangedValuesDeep } from "helpers/getChangedValues";

import { LandingPageEditorModal } from "./LandingPageEditorModal";
import { createInitialValues } from "./constants";
import type { FormValues } from "./types";

export interface LandingPageEditorFormWrapperProps {
  isOpen: boolean;
  onClose?: () => void;
  onSave?: (content: { brief: string; overview: string; faq: string }) => void;
  campaignId: string;
}

export const LandingPageEditorFormWrapper = ({
  isOpen,
  onClose,
  onSave,
  campaignId,
}: LandingPageEditorFormWrapperProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: existingLandings, isLoading: isLoadingLandings }
    = useGetCampaignLandingsQuery(
      { campaign_id: campaignId },
      { skip: !campaignId },
    );

  const existingLanding = Array.isArray(existingLandings)
    ? existingLandings.find(
      (landing: any) => landing.campaign_id === campaignId,
    )
    : null;

  const [createCampaignLanding] = useCreateCampaignLandingMutation();
  const [updateCampaignLanding] = useUpdateCampaignLandingMutation();

  const initialValues = useMemo(() => {
    return createInitialValues(
      existingLanding?.brief?.length ? existingLanding.brief : undefined,
      existingLanding?.overview?.length ? existingLanding.overview : undefined,
      existingLanding?.faq?.length ? existingLanding.faq : undefined,
    );
  }, [existingLanding]);

  const handleSubmit = useCallback(
    async(values: FormValues) => {
      try {
        const changedValues = getChangedValuesDeep(values, initialValues);

        if (Object.keys(changedValues).length === 0) {
          setHasUnsavedChanges(false);
          onClose?.();
          return;
        }

        const apiData = {
          campaign_id: campaignId,
          name: `Landing Page for Campaign ${campaignId}`,
          status: "active",
          ...changedValues,
        };

        if (existingLanding?._id) {
          await updateCampaignLanding({
            _id: existingLanding._id,
            ...apiData,
          }).unwrap();
        } else {
          await createCampaignLanding(apiData).unwrap();
        }

        setHasUnsavedChanges(false);

        // Notify parent component if needed (only include changed values)
        if (Object.keys(changedValues).length > 0) {
          const saveData: { brief?: string; overview?: string; faq?: string }
            = {};
          if (changedValues.brief) {
            saveData.brief = JSON.stringify(changedValues.brief);
          }
          if (changedValues.overview) {
            saveData.overview = JSON.stringify(changedValues.overview);
          }
          if (changedValues.faq) {
            saveData.faq = JSON.stringify(changedValues.faq);
          }
          onSave?.(
            saveData as { brief: string; overview: string; faq: string },
          );
        }

        onClose?.();
      } catch (error) {
        console.error("Failed to save landing page:", error);
        // TODO: Add proper error handling/toasts
      }
    },
    [
      campaignId,
      existingLanding,
      createCampaignLanding,
      updateCampaignLanding,
      onSave,
      onClose,
      initialValues,
    ],
  );

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes to the landing page content. Are you sure you want to close without saving?",
      );
      if (!confirmed) {
        return;
      }
    }

    setHasUnsavedChanges(false);
    onClose?.();
  }, [hasUnsavedChanges, onClose]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={true} // Allow form to update when API data loads
    >
      <LandingPageEditorModal
        isOpen={isOpen}
        onClose={handleClose}
        // campaignId={campaignId}
        hasUnsavedChanges={hasUnsavedChanges}
        setHasUnsavedChanges={setHasUnsavedChanges}
        isLoading={isLoadingLandings}
      />
    </Formik>
  );
};
